import { useMemo, useState, type FormEvent } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useRingGroups, useSaveRingGroup, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { useAdminGroups } from '../../lib/api/hooks-extension.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, Segmented } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import type { RingGroup, Destination } from '@arcnet/contracts';

type Strategy = RingGroup['strategy'];

/**
 * Hunt lists: several people rung for one call.
 *
 * Three strategies and only three, because they are what a hunt list can do on
 * its own: everybody at once, one after another in the order listed, or one
 * after another in an order drawn for each call. Round robin and longest idle
 * need to remember who took the last call, which is a queue's job; they were
 * offered here once and rang everybody at once (docs/26 D6).
 */
const STRATEGIES: Array<[Strategy, string]> = [
  ['simultaneous', 'All at once'], ['sequential', 'In order'], ['random', 'Random order'],
];
const STRATEGY_LABEL = new Map<string, string>(STRATEGIES);

const STRATEGY_HINT: Record<Strategy, string> = {
  simultaneous: 'Everybody rings together; the first to answer takes the call.',
  sequential: 'One at a time, from the top of the list.',
  random: 'One at a time, in a different order on every call.',
};

/** The cost-centre filter's value for people in none; it cannot collide with an id. */
const UNASSIGNED = '__unassigned__';

export function RingGroupsTab({ tenantId }: { tenantId: string }) {
  // Creating, editing and deleting need ringgroup:write, as the API requires (permission sweep, 19 September 2026).
  const mayWrite = useSession((s) => s.can)('ringgroup:write');
  const groups = useRingGroups(tenantId);
  const del = useDeleteTms(tenantId, 'ring-groups');
  const [editing, setEditing] = useState<RingGroup | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Hunt lists</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Ring several people for one call: together, in order, or in a random order.
          </p>
        </div>
        <div className="arc-spacer" />
        {mayWrite && (
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New hunt list'}
        </button>
        )}
      </div>

      {groups.error && <ErrorBanner error={groups.error} />}
      {(creating || editing) && (
        <RingGroupEditor key={editing?.id ?? 'new'} tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Rings</th><th>Members</th>
                <th className="arc-td-num">Ring time</th><th>If nobody answers</th><th />
              </tr>
            </thead>
            {groups.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {groups.data?.data.map((g) => (
                  <tr key={g.id} className="arc-row-editable" onDoubleClick={() => { setEditing(g); setCreating(false); }}>
                    <td style={{ fontWeight: 550 }}>{g.name}</td>
                    <td className="arc-mono">{g.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td><Badge tone="muted">{STRATEGY_LABEL.get(g.strategy) ?? g.strategy}</Badge></td>
                    <td>
                      <span className="arc-muted">{g.members.length}</span>
                      {g.members.length > 0 && (
                        <span className="arc-subtle">
                          {' · '}{g.members.slice(0, 3).map((m) => m.extension_number ?? m.external_number).join(', ')}
                          {g.members.length > 3 ? ' …' : ''}
                        </span>
                      )}
                    </td>
                    <td className="arc-td-num">
                      {g.ring_time_sec}s{g.strategy === 'simultaneous' ? '' : ' each'}
                    </td>
                    <td><DestinationLabel tenantId={tenantId} destination={g.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        {mayWrite && (
                        <button type="button" className="arc-btn arc-btn-sm arc-pill-edit"
                          onClick={() => { setEditing(g); setCreating(false); }}>Edit</button>
                        )}
                        {mayWrite && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete hunt list '${g.name}'?`)) del.mutate(g.id); }}>
                          Delete
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {groups.data !== undefined && groups.data?.data.length === 0 && (
          <Empty icon="users" title="No hunt lists"
            hint="A hunt list rings a group of people for one incoming call." />
        )}
      </div>
    </>
  );
}

function RingGroupEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: RingGroup | null; onDone: () => void;
}) {
  const save = useSaveRingGroup(tenantId);
  // Everybody, not the first page: a list of five hundred is picked from the whole account.
  const extensions = useExtensions(tenantId, { limit: 1000 });
  const costCentres = useAdminGroups(tenantId);

  const [name, setName] = useState(existing?.name ?? '');
  const [code, setCode] = useState(existing?.extension ?? '');
  const [strategy, setStrategy] = useState<Strategy>(existing?.strategy ?? 'simultaneous');
  const [ringTime, setRingTime] = useState(String(existing?.ring_time_sec ?? 20));
  const [timeout_, setTimeout_] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [members, setMembers] = useState<string[]>(
    () => existing?.members.flatMap((m) => (m.extension_id ? [m.extension_id] : [])) ?? []);
  /**
   * Outside numbers on the list, which this editor does not offer, go back as
   * they are. Saving replaces the whole member list, and a list edited here must
   * not lose a mobile somebody added another way.
   */
  const outside = existing?.members.filter((m) => !m.extension_id && m.external_number) ?? [];

  const [search, setSearch] = useState('');
  const [centre, setCentre] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set());

  const everyone = useMemo(() => extensions.data?.data ?? [], [extensions.data]);
  const byId = useMemo(() => new Map(everyone.map((e) => [e.id, e])), [everyone]);
  const inList = new Set(members);
  const q = search.trim().toLowerCase();
  const addable = everyone.filter((e) =>
    !inList.has(e.id)
    && (centre === '' || (centre === UNASSIGNED ? !e.admin_group_id : e.admin_group_id === centre))
    && (q === '' || e.number.includes(q) || e.display_name.toLowerCase().includes(q)));

  const ordered = strategy === 'sequential';

  const add = (ids: string[]) => {
    setMembers((m) => [...m, ...ids.filter((id) => !m.includes(id))]);
    setPicked(new Set());
  };
  const move = (i: number, by: -1 | 1) => setMembers((m) => {
    const j = i + by;
    if (j < 0 || j >= m.length) return m;
    const next = [...m];
    [next[i], next[j]] = [next[j]!, next[i]!];
    return next;
  });
  const togglePick = (id: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      ...(existing ? { id: existing.id } : {}),
      name, extension: code || null, strategy, ring_time_sec: Number(ringTime),
      skip_busy: true,
      timeout_destination: timeout_,
      // Position is the ringing order for In order, so it follows the list as shown.
      members: [
        ...members.map((id, i) => ({ extension_id: id, position: i, delay_sec: 0 })),
        ...outside.map((m, i) => ({
          external_number: m.external_number!, position: members.length + i, delay_sec: m.delay_sec,
        })),
      ],
    }, { onSuccess: onDone });
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New hunt list'}
      </h3>

      <div style={{
        display: 'grid', gap: 12, marginBottom: 6,
        gridTemplateColumns: 'minmax(170px, 1.3fr) minmax(110px, 0.5fr) minmax(250px, 1.4fr) minmax(110px, 0.6fr)',
      }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-name">Name</label>
          <input id="rg-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Sales team" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-code">Dial code</label>
          <input id="rg-code" className="arc-input arc-mono" inputMode="numeric" maxLength={6}
            value={code} placeholder="none"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <span className="arc-label">Rings</span>
          <Segmented label="How the list rings" value={strategy} options={STRATEGIES}
            onChange={setStrategy} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-time">
            {strategy === 'simultaneous' ? 'Ring for (s)' : 'Ring each for (s)'}
          </label>
          <input id="rg-time" className="arc-input" type="number" min={5} max={300}
            value={ringTime} onChange={(e) => setRingTime(e.target.value)} />
        </div>
      </div>
      {/*
        Both said where they are set, because both have been misread: the dial
        code as something that makes a feature code, and the strategy as a
        promise the switch was not keeping.
      */}
      <p className="arc-hint" style={{ marginBottom: 14 }}>
        {STRATEGY_HINT[strategy]} The dial code is optional: colleagues dial it to reach the list.
        It shares numbers with extensions, queues, IVR menus and conference rooms, and it is not a
        feature code.
      </p>

      <div className="arc-field">
        <span className="arc-label">Members</span>
        <div className="arc-pick-grid">
          <div className="arc-pick-pane">
            <div className="arc-pick-head">
              <div className="arc-search" style={{ flex: '1 1 160px' }}>
                <Icon name="search" size={14} />
                <input className="arc-input" placeholder="Search name or number"
                  aria-label="Search extensions" value={search}
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select className="arc-select" style={{ flex: '0 1 180px' }} aria-label="Department"
                value={centre} onChange={(e) => setCentre(e.target.value)}>
                <option value="">All departments</option>
                {(costCentres.data?.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
                <option value={UNASSIGNED}>No department</option>
              </select>
            </div>
            <div className="arc-pick-list">
              {extensions.data === undefined ? <TableSkeleton rows={6} cols={1} /> : addable.length === 0 ? (
                <p className="arc-subtle" style={{ padding: '10px 12px', margin: 0 }}>
                  {everyone.length === 0 ? 'No extensions yet.' : 'Nobody else matches.'}
                </p>
              ) : addable.map((e) => (
                <label key={e.id} className="arc-pick-row" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={picked.has(e.id)} onChange={() => togglePick(e.id)} />
                  <span className="arc-mono">{e.number}</span>
                  <span className="arc-truncate" style={{ flex: 1 }}>{e.display_name}</span>
                  {e.admin_group_name && (
                    <span className="arc-subtle" style={{ fontSize: 11.5 }}>{e.admin_group_name}</span>
                  )}
                </label>
              ))}
            </div>
            <div className="arc-pick-foot">
              <button type="button" className="arc-btn arc-btn-sm" disabled={picked.size === 0}
                onClick={() => add([...picked])}>
                <Icon name="plus" size={12} /> Add{picked.size > 0 ? ` ${picked.size}` : ''}
              </button>
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={addable.length === 0}
                onClick={() => add(addable.map((e) => e.id))}>
                Add all {addable.length}
              </button>
            </div>
          </div>

          <div className="arc-pick-pane">
            <div className="arc-pick-head">
              <strong style={{ fontSize: 12.5 }}>
                {members.length} {members.length === 1 ? 'member' : 'members'}
              </strong>
              <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                {ordered ? 'ringing from the top' : strategy === 'random' ? 'order drawn per call' : 'ringing together'}
              </span>
              <div className="arc-spacer" />
              {members.length > 0 && (
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setMembers([])}>Remove all</button>
              )}
            </div>
            <div className="arc-pick-list">
              {members.length === 0 ? (
                <p className="arc-subtle" style={{ padding: '10px 12px', margin: 0 }}>
                  Nobody yet. Pick people on the left.
                </p>
              ) : members.map((id, i) => {
                const ext = byId.get(id);
                const who = ext?.number ?? 'member';
                return (
                  <div key={id} className="arc-pick-row">
                    {ordered && <span className="arc-pick-pos">{i + 1}</span>}
                    <span className="arc-mono">{ext?.number ?? '—'}</span>
                    <span className="arc-truncate" style={{ flex: 1 }}>
                      {ext?.display_name ?? <span className="arc-subtle">extension no longer exists</span>}
                    </span>
                    {ordered && (
                      <>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          aria-label={`Move ${who} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                          <Icon name="chevronUp" size={12} />
                        </button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                          aria-label={`Move ${who} down`} disabled={i === members.length - 1}
                          onClick={() => move(i, 1)}>
                          <Icon name="chevronDown" size={12} />
                        </button>
                      </>
                    )}
                    <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                      aria-label={`Remove ${who}`}
                      onClick={() => setMembers((m) => m.filter((x) => x !== id))}>
                      <Icon name="close" size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
            {outside.length > 0 && (
              <div className="arc-pick-foot">
                <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                  Also rings {outside.map((m) => m.external_number).join(', ')}, kept as set.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="arc-field">
        <label className="arc-label">If nobody answers</label>
        <DestinationPicker tenantId={tenantId} value={timeout_} onChange={setTimeout_} allowNone />
      </div>

      <div className="arc-row" style={{ gap: 8 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create hunt list'}
        </button>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
